import { useState, useEffect, useRef, useMemo } from "react";
import { Table as TableIcon, ChevronLeft, ChevronRight, ChevronDown, Check } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart as RePieChart,
  Pie,
  Cell,
  LineChart as ReLineChart,
  Line,
  AreaChart as ReAreaChart,
  Area,
  Sector,
} from "recharts";

const SERIES_VARS = [
  "#f97316",
  "#3b82f6", // blue-500
  "#10b981", // emerald-500
  "#8b5cf6", // violet-500
  "#ef4444", // red-500
  "#ec4899", // pink-500
  "#14b8a6", // teal-500
  "#f59e0b", // amber-500
];
const MAX_SERIES = 8;

export function fmtNum(v: any): string {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function cleanLbl(v: any): string {
  if (v === null || v === undefined) return "";
  const s = String(v).replace(/_/g, " ");
  return s.replace(/\b\w/g, (char) => char.toUpperCase());
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-gray-850/95 backdrop-blur-sm border border-gray-750/50 rounded-lg p-2.5 shadow-2xl text-[10px] text-white flex flex-col gap-1.5 min-w-[130px] z-50">
        {label !== undefined && label !== null && label !== "" && (
          <div className="text-gray-300 font-bold border-b border-gray-750/80 pb-1 mb-0.5 text-[11px] truncate">
            {cleanLbl(label)}
          </div>
        )}
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="flex items-center gap-1.5 text-gray-300 font-medium">
              <span
                className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                style={{ backgroundColor: entry.color || entry.stroke || "#f97316" }}
              />
              {cleanLbl(entry.name)}
            </span>
            <span className="font-bold text-white font-mono">
              {entry.value?.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const lbl = (v: any) => (v === null || v === undefined || v === "" ? "(none)" : String(v));

export function shape(result: any, qdef: any) {
  const hasX = !!qdef?.x_axis;
  const hasSplit = !!qdef?.split_series;
  const metricCount = (qdef?.metrics || []).length;
  const columns = Array.isArray(result?.columns) ? result.columns : [];
  const rows = Array.isArray(result?.rows) ? result.rows : [];
  const metricNames = columns.slice((hasX ? 1 : 0) + (hasSplit ? 1 : 0));

  if (!hasX && !hasSplit) {
    return { kind: "scalar", metricNames, row: rows[0] || [] };
  }

  if (hasX && hasSplit) {
    const xs: string[] = [];
    const seriesNames: string[] = [];
    const cell = new Map<string, number>();
    for (const row of rows) {
      const x = lbl(row[0]);
      let s = lbl(row[1]);
      if (!xs.includes(x)) xs.push(x);
      if (!seriesNames.includes(s)) {
        if (seriesNames.length >= MAX_SERIES) s = "Other";
        if (!seriesNames.includes(s)) seriesNames.push(s);
      }
      const k = x + " " + s;
      cell.set(k, (cell.get(k) || 0) + Number(row[2] ?? 0));
    }
    const series = seriesNames.map((name: string) => ({
      name,
      values: xs.map((x: string) => cell.get(x + " " + name) ?? null),
    }));
    return {
      kind: "xy",
      xs,
      series,
      metricNames: [metricNames[metricCount ? metricNames.length - metricCount : 0]],
    };
  }

  const xs = rows.map((r: any) => lbl(r[0]));
  const series = metricNames.map((name: string, mi: number) => ({
    name,
    values: rows.map((r: any) => (r[1 + mi] === null ? null : Number(r[1 + mi]))),
  }));
  return { kind: "xy", xs, series, metricNames };
}



interface TooltipInfo {
  x: number;
  y: number;
  title: string | null;
  rows: Array<{ name: string; value: string; color?: string }>;
}

const renderActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  const rad = Math.PI / 180;
  const midAngle = (startAngle + endAngle) / 2;
  const sin = Math.sin(-rad * midAngle);
  const cos = Math.cos(-rad * midAngle);
  const mx = cx + 6 * cos;
  const my = cy + 6 * sin;
  return (
    <g>
      <Sector
        cx={mx}
        cy={my}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 2}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        style={{
          filter: "drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.45))",
          transition: "all 0.2s ease-out",
        }}
      />
    </g>
  );
};

function CustomPageSizeSelect({
  value,
  onChange,
  options = [5, 10, 20, 50],
}: {
  value: number;
  onChange: (newSize: number) => void;
  options?: number[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block text-left" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-gray-300 bg-gray-800 hover:bg-gray-750 border border-gray-700 hover:border-gray-600 rounded transition-colors shadow-sm focus:outline-none"
      >
        <span>{value} / page</span>
        <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 bottom-full mb-1 z-50 min-w-[95px] bg-gray-900 border border-gray-750 rounded-lg shadow-xl py-1 overflow-hidden">
          {options.map((opt) => {
            const isSelected = opt === value;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  onChange(opt);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-2.5 py-1 text-[10px] font-medium transition-colors ${isSelected ? "bg-orange-500/15 text-orange-400 font-semibold" : "text-gray-300 hover:bg-gray-800 hover:text-white"
                  }`}
              >
                <span>{opt} / page</span>
                {isSelected && <Check className="w-3 h-3 text-orange-400 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TableWidgetComponent({
  queryResult,
  onPageChange,
}: {
  queryResult: any;
  onPageChange?: (skip: number, limit: number) => void;
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const columns: string[] = Array.isArray(queryResult?.columns)
    ? queryResult.columns
    : Array.isArray(queryResult?.data?.columns)
      ? queryResult.data.columns
      : [];
  const rows: any[] = Array.isArray(queryResult?.rows)
    ? queryResult.rows
    : Array.isArray(queryResult?.data?.rows)
      ? queryResult.data.rows
      : [];

  const totalCount =
    queryResult?.total ??
    queryResult?.total_count ??
    queryResult?.data?.total ??
    queryResult?.data?.total_count ??
    queryResult?.unfiltered_total ??
    queryResult?.unfiltered_count ??
    rows.length;

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(currentPage, totalPages);

  const isServerPaginated = rows.length <= pageSize && totalCount > rows.length;
  const startIndex = (safePage - 1) * pageSize;
  const displayRows = isServerPaginated ? rows : rows.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    const serverSkip = queryResult?.skip ?? queryResult?.data?.skip ?? queryResult?.offset;
    if (typeof serverSkip === "number" && pageSize > 0) {
      const expectedPage = Math.floor(serverSkip / pageSize) + 1;
      setCurrentPage(expectedPage);
    }
  }, [queryResult, pageSize]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    if (onPageChange) {
      const skip = (newPage - 1) * pageSize;
      onPageChange(skip, pageSize);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    if (onPageChange) {
      onPageChange(0, newSize);
    }
  };

  if (columns.length === 0 && rows.length === 0) {
    return (
      <div className="text-gray-500 text-xs p-4 flex flex-col items-center justify-center h-full text-center">
        <TableIcon className="w-6 h-6 text-gray-600 mb-2" />
        <span>No columns or records returned</span>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col overflow-hidden text-gray-300 rounded-lg border border-gray-800/80 bg-gray-950/20">
      <div className="flex-1 overflow-auto custom-scroll">
        <table className="w-full text-left text-xs border-collapse table-auto min-w-full">
          <thead className="bg-gray-900 sticky top-0 z-10 border-b border-gray-750">
            <tr>
              {columns.map((c: string) => (
                <th
                  key={c}
                  className="px-4 py-2.5 font-bold text-gray-200 uppercase tracking-wider border-r border-gray-750 last:border-r-0 text-[10px] bg-gray-850 whitespace-nowrap"
                >
                  {cleanLbl(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-750/50">
            {displayRows.map((row: any, i: number) => (
              <tr key={i} className="hover:bg-gray-800/40 transition-colors even:bg-gray-850/15 odd:bg-gray-900/5">
                {columns.map((c: string, j: number) => {
                  const val = Array.isArray(row) ? row[j] : (row[c] !== undefined ? row[c] : row[j]);
                  return (
                    <td
                      key={j}
                      className="px-4 py-2 truncate max-w-[260px] text-[11px] font-medium text-gray-300 border-r border-gray-750/30 last:border-r-0"
                      title={val !== null && val !== undefined ? String(val) : ""}
                    >
                      {val === null || val === undefined || val === "" ? (
                        <span className="text-gray-650">—</span>
                      ) : typeof val === "boolean" ? (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${val ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`}>
                          {val ? "True" : "False"}
                        </span>
                      ) : typeof val === "object" ? (
                        JSON.stringify(val)
                      ) : (
                        String(val)
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalCount > 0 && (
        <div className="px-3 py-1.5 bg-gray-900/90 border-t border-gray-800 text-[11px] text-gray-400 flex items-center justify-between shrink-0 font-medium select-none relative z-20">
          <div className="flex items-center gap-2 text-[10px]">
            <span>
              Showing {totalCount > 0 ? (isServerPaginated ? (safePage - 1) * pageSize + 1 : startIndex + 1) : 0}–
              {Math.min(isServerPaginated ? (safePage - 1) * pageSize + displayRows.length : startIndex + pageSize, totalCount)} of {totalCount}
            </span>
            <CustomPageSizeSelect value={pageSize} onChange={handlePageSizeChange} />
          </div>
          <div className="flex items-center gap-1.5 text-[10px]">
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => handlePageChange(Math.max(1, safePage - 1))}
              className="p-1 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-gray-300 hover:text-white border border-gray-700 transition-colors"
              title="Previous Page"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <span className="px-1 font-semibold text-gray-300">
              {safePage} / {totalPages}
            </span>
            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => handlePageChange(Math.min(totalPages, safePage + 1))}
              className="p-1 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-gray-300 hover:text-white border border-gray-700 transition-colors"
              title="Next Page"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WidgetRendererV2({
  widget,
  queryResult,
  onPageChange,
}: {
  widget: any;
  queryResult: any;
  onPageChange?: (skip: number, limit: number) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 300, height: 200 });
  const [tooltip, setTooltip] = useState<TooltipInfo | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);
  const [hoveredCell, setHoveredCell] = useState<{ xi: number; yi: number } | null>(null);
  const [isGaugeHovered, setIsGaugeHovered] = useState(false);

  const visConfig = useMemo(() => {
    let vis = widget?.visualization_config;
    if (typeof vis === "string") {
      try {
        vis = JSON.parse(vis);
      } catch {
        vis = {};
      }
    }
    return vis || {};
  }, [widget?.visualization_config]);

  const xAxisLabel = visConfig.x_axis_label || "";
  const yAxisLabel = visConfig.y_axis_label || "";
  const gaugeLabel = visConfig.gauge_label || visConfig.label || visConfig.subtitle || "";

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setSize({
          width: Math.max(50, entry.contentRect.width),
          height: Math.max(50, entry.contentRect.height),
        });
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const d = useMemo(() => {
    if (!queryResult || !queryResult.rows || widget.chart_type === "table") return null;
    return shape(queryResult, widget.query_definition);
  }, [queryResult, widget]);

  const chartType = widget.chart_type;

  const legendList = useMemo(() => {
    if (!d || d.kind !== "xy") return [];
    if (chartType === "pie" || chartType === "donut") {
      const s0 = d.series[0];
      if (!s0) return [];
      let items = d.xs
        .map((x: any, i: number) => ({ name: x, value: Number(s0.values[i] ?? 0) }))
        .filter((it: any) => it.value > 0);
      items.sort((a: any, b: any) => b.value - a.value);
      if (items.length > MAX_SERIES) {
        const rest = items.slice(MAX_SERIES - 1).reduce((a: any, b: any) => a + b.value, 0);
        items = items.slice(0, MAX_SERIES - 1).concat({ name: "Other", value: rest });
      }
      return items;
    }
    return d.series.length >= 2 ? d.series : [];
  }, [d, chartType]);

  if (!queryResult || (!queryResult.rows && !queryResult.data?.rows)) {
    return <div className="text-gray-500 text-xs p-4 flex items-center justify-center h-full">No data available</div>;
  }

  // 1. Table Renderer
  if (chartType === "table") {
    return <TableWidgetComponent queryResult={queryResult} onPageChange={onPageChange} />;
  }

  if (!d) return null;

  if (d.kind === "scalar" && chartType !== "metric" && chartType !== "gauge") {
    return (
      <div className="text-gray-400 text-xs p-4 flex flex-col items-center justify-center h-full text-center gap-1">
        <span className="font-semibold text-gray-300">X-axis required</span>
        <span>Please configure an X-axis to render this chart type.</span>
      </div>
    );
  }

  if (d.kind === "xy" && !d.xs.length) {
    return <div className="text-gray-500 text-xs p-4 flex items-center justify-center h-full">No rows returned</div>;
  }

  // Render tooltip
  const renderTooltip = () => {
    if (!tooltip) return null;
    return (
      <div
        className="fixed pointer-events-none bg-gray-900 border border-gray-700 rounded-lg p-2 shadow-xl text-xs text-white z-[9999] flex flex-col gap-1 min-w-[140px]"
        style={{ left: tooltip.x + 12, top: tooltip.y + 12 }}
      >
        {tooltip.title && <div className="text-gray-400 font-bold border-b border-gray-800 pb-1 mb-1">{cleanLbl(tooltip.title)}</div>}
        {tooltip.rows.map((r: any, i: number) => (
          <div key={i} className="flex justify-between items-center gap-4">
            <span className="flex items-center gap-1.5 text-gray-300">
              {r.color && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: r.color }} />}
              {cleanLbl(r.name)}
            </span>
            <span className="font-bold text-white font-mono">{r.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderLegend = (seriesList: Array<{ name: string }>) => {
    if (seriesList.length < 1) return null;
    return (
      <div className="absolute top-1.5 right-2 z-10 flex flex-col gap-y-1 items-start select-none text-[9px] font-semibold text-gray-300 bg-gray-950/85 backdrop-blur-md border border-gray-800/80 rounded-lg p-1.5 shadow-xl max-h-[85px] overflow-y-auto custom-scroll">
        {seriesList.map((s: any, i: number) => {
          const sName = cleanLbl(s.name);
          let isHovered = hoveredSeries !== null && cleanLbl(hoveredSeries) === sName;

          if (chartType === "pie" || chartType === "donut") {
            const currentActiveIndex = activeIndex;
            if (currentActiveIndex !== null && d && d.kind === "xy") {
              const s0 = d.series[0];
              const pieItems = d.xs
                .map((x: any, idx: number) => ({ name: cleanLbl(x), value: Number(s0?.values[idx] ?? 0) }))
                .filter((it: any) => it.value > 0);
              if (pieItems[currentActiveIndex] && cleanLbl(pieItems[currentActiveIndex].name) === sName) {
                isHovered = true;
              }
            }
          }

          if ((chartType === "bar" || chartType === "horizontal_bar") && d && d.series.length === 1) {
            const currentHoveredIndex = hoveredIndex;
            if (currentHoveredIndex !== null && d.xs[currentHoveredIndex]) {
              if (cleanLbl(d.xs[currentHoveredIndex]) === sName) {
                isHovered = true;
              }
            }
          }

          const isAnyHovered = hoveredSeries !== null || activeIndex !== null || hoveredIndex !== null;
          const color = SERIES_VARS[i % SERIES_VARS.length];

          return (
            <div
              key={s.name || i}
              className={`flex items-center gap-1.5 px-1.5 py-0.5 rounded-md cursor-pointer transition-all duration-200 ${isHovered
                ? "bg-gray-800/90 text-white font-bold shadow-md ring-1 ring-gray-600/50"
                : isAnyHovered
                  ? "opacity-35 text-gray-400 hover:opacity-100"
                  : "hover:bg-gray-800/50 hover:text-white"
                }`}
              style={{
                transform: isHovered ? "translateX(3px) scale(1.04)" : "none",
              }}
              onMouseEnter={() => setHoveredSeries(sName)}
              onMouseLeave={() => setHoveredSeries(null)}
            >
              <span
                className={`w-2 h-2 rounded-full shrink-0 transition-all duration-200 ${isHovered ? "scale-125 ring-2 ring-white/50 shadow-[0_0_8px_rgba(255,255,255,0.7)]" : ""
                  }`}
                style={{ backgroundColor: color }}
              />
              <span className="truncate max-w-[90px]" title={sName}>{sName}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // Render specific chart types
  const renderChartContent = () => {
    const W = size.width;
    const H = size.height;

    // 2. Metric Chart
    if (chartType === "metric") {
      const v = d.kind === "scalar" ? d.row[0] : d.series[0]?.values[0];
      return (
        <div className="flex flex-col justify-center items-center h-full gap-1">
          <div className="text-4xl font-bold text-white font-mono tracking-tight">{fmtNum(v)}</div>
          <div className="text-xs text-gray-400 font-medium truncate max-w-full">
            {cleanLbl(widget.visualization_config?.subtitle || d.metricNames[0] || "Value")}
          </div>
        </div>
      );
    }

    // 3. Gauge Chart
    if (chartType === "gauge") {
      const v = Number(d.kind === "scalar" ? d.row[0] : d.series[0]?.values[0]) || 0;
      const unfilteredTotal =
        queryResult?.unfiltered_total ??
        queryResult?.data?.unfiltered_total ??
        queryResult?.unfiltered_count ??
        queryResult?.data?.unfiltered_count ??
        queryResult?.total ??
        queryResult?.data?.total;

      const max =
        unfilteredTotal !== undefined && unfilteredTotal !== null && !isNaN(Number(unfilteredTotal))
          ? Number(unfilteredTotal)
          : Number(widget.visualization_config?.max) || Math.max(v, 100);

      const frac = max > 0 ? Math.min(1, Math.max(0, v / max)) : 0;
      const clampFrac = Math.min(0.9999, frac);

      const topPadding = 12;
      const bottomPadding = gaugeLabel ? 30 : 16;
      const R = Math.max(16, Math.min((W - 24) / 2, H - topPadding - bottomPadding));
      const cx = W / 2;
      const cy = topPadding + R;

      const arc = (f: number, r: number) => {
        const a = Math.PI + f * Math.PI;
        return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
      };

      const [ex, ey] = arc(clampFrac, R);
      const cleanGaugeLabel = cleanLbl(gaugeLabel);
      const displayGaugeLabel = cleanGaugeLabel.length > 28 ? cleanGaugeLabel.slice(0, 26) + "…" : cleanGaugeLabel;

      const mainFontSize = Math.min(22, Math.max(11, R * 0.38));
      const subFontSize = Math.min(10, Math.max(8, R * 0.16));
      const labelFontSize = Math.min(11, Math.max(8, R * 0.18));

      return (
        <svg
          className="w-full h-full select-none cursor-pointer"
          onMouseEnter={() => setIsGaugeHovered(true)}
          onMouseLeave={() => setIsGaugeHovered(false)}
        >
          <path
            d={`M${cx - R} ${cy} A${R} ${R} 0 0 1 ${cx + R} ${cy}`}
            fill="none"
            stroke="#9c9d9fff"
            strokeWidth={isGaugeHovered ? "12" : "10"}
            strokeLinecap="round"
            style={{ transition: "stroke-width 0.2s ease" }}
          />
          {frac > 0.005 && (
            <path
              d={`M${cx - R} ${cy} A${R} ${R} 0 0 1 ${ex} ${ey}`}
              fill="none"
              stroke="#f97316"
              strokeWidth={isGaugeHovered ? "12" : "10"}
              strokeLinecap="round"
              style={{
                transition: "all 0.2s ease",
                filter: isGaugeHovered
                  ? "drop-shadow(0px 0px 8px rgba(249, 115, 22, 0.7))"
                  : "none",
              }}
            />
          )}
          <text
            x={cx}
            y={cy - R * 0.18}
            textAnchor="middle"
            fill="#ffffff"
            fontSize={mainFontSize}
            fontWeight="700"
            className="font-mono"
          >
            {fmtNum(v)}
          </text>
          <text
            x={cx}
            y={cy + Math.min(12, subFontSize + 2)}
            textAnchor="middle"
            fill="#9ca3af"
            fontSize={subFontSize}
            fontWeight="500"
          >
            of {fmtNum(max)}
          </text>
          {gaugeLabel && (
            <text
              x={cx}
              y={cy + Math.min(26, subFontSize + labelFontSize + 6)}
              textAnchor="middle"
              fill="#d1d5db"
              fontSize={labelFontSize}
              fontWeight="600"
            >
              <title>{cleanGaugeLabel}</title>
              {displayGaugeLabel}
            </text>
          )}
        </svg>
      );
    }

    // 4. Heatmap Chart
    if (chartType === "heatmap") {
      const ys = d.series.map((s: any) => s.name);
      const m = { t: 4, r: 4, b: xAxisLabel ? 24 : 20, l: Math.min(120, W * 0.3) };
      const cw = (W - m.l - m.r) / d.xs.length;
      const ch = (H - m.t - m.b) / ys.length;
      const max = Math.max(1, ...d.series.flatMap((s: any) => s.values.filter((v: any) => v !== null)));

      return (
        <svg className="w-full h-full select-none">
          {ys.map((y: any, yi: number) => {
            const cleanY = cleanLbl(y);
            const short = cleanY.length > 18 ? cleanY.slice(0, 17) + "…" : cleanY;
            return (
              <g key={y}>
                <text x={m.l - 6} y={m.t + ch * yi + ch / 2 + 3} textAnchor="end" fill="#d1d5db" fontSize="10" fontWeight="600">
                  {short}
                </text>
                {d.xs.map((x: any, xi: number) => {
                  const v = d.series[yi].values[xi];
                  const f = v === null || v === undefined ? 0 : v / max;
                  const color =
                    f === 0
                      ? "#1f2937"
                      : `rgba(249, 115, 22, ${0.15 + f * 0.85})`; // Orange color scale

                  const isCellHovered = hoveredCell?.xi === xi && hoveredCell?.yi === yi;
                  const isAnyCellHovered = hoveredCell !== null;
                  return (
                    <rect
                      key={x}
                      x={m.l + cw * xi + 1}
                      y={m.t + ch * yi + 1}
                      width={Math.max(1, cw - 2)}
                      height={Math.max(1, ch - 2)}
                      rx="2"
                      fill={color}
                      stroke={isCellHovered ? "#ffffff" : "none"}
                      strokeWidth={isCellHovered ? 1.5 : 0}
                      style={{
                        transition: "all 0.15s ease",
                        opacity: isAnyCellHovered && !isCellHovered ? 0.5 : 1,
                      }}
                      className="cursor-pointer hover:brightness-125"
                      onMouseMove={(e) => {
                        setHoveredCell({ xi, yi });
                        setTooltip({
                          x: e.clientX,
                          y: e.clientY,
                          title: `${x} × ${y}`,
                          rows: [{ name: d.metricNames[0] || "Value", value: fmtNum(v ?? 0) }],
                        });
                      }}
                      onMouseLeave={() => {
                        setHoveredCell(null);
                        setTooltip(null);
                      }}
                    />
                  );
                })}
              </g>
            );
          })}
          {d.xs.map((x: any, xi: number) => {
            if (d.xs.length <= 10 || xi % Math.ceil(d.xs.length / 10) === 0) {
              const cleanX = cleanLbl(x);
              const short = cleanX.length > 9 ? cleanX.slice(0, 8) + "…" : cleanX;
              return (
                <text key={x} x={m.l + cw * xi + cw / 2} y={H - (xAxisLabel ? 14 : 5)} textAnchor="middle" fill="#9ca3af" fontSize="9">
                  {short}
                </text>
              );
            }
            return null;
          })}
          {xAxisLabel && (
            <text x={m.l + (W - m.l - m.r) / 2} y={H - 2} textAnchor="middle" fill="#9ca3af" fontSize="9" fontWeight="600">
              {xAxisLabel}
            </text>
          )}
        </svg>
      );
    }

    // Build data for Recharts (Bar, Line, Area, Pie, Donut)
    const rechartsData = d.xs.map((x: any, idx: number) => {
      const item: any = { name: cleanLbl(x) };
      d.series.forEach((s: any) => {
        item[s.name] = s.values[idx] ?? 0;
      });
      return item;
    });
    const valueKeys = d.series.map((s: any) => s.name);

    // 5. Pie / Donut Chart
    if (chartType === "pie" || chartType === "donut") {
      const s0 = d.series[0];
      const pieData = d.xs
        .map((x: any, idx: number) => {
          const val = Number(s0?.values[idx] ?? 0);
          return {
            name: cleanLbl(x),
            value: val,
            fill: SERIES_VARS[idx % SERIES_VARS.length],
          };
        })
        .filter((item: any) => item.value > 0);

      const total = pieData.reduce((acc: number, item: any) => acc + item.value, 0);

      let pieActiveIndex = activeIndex;
      if (pieActiveIndex === null && hoveredSeries !== null) {
        const foundIdx = pieData.findIndex((item: any) => cleanLbl(item.name) === cleanLbl(hoveredSeries));
        if (foundIdx !== -1) {
          pieActiveIndex = foundIdx;
        }
      }

      return (
        <div className="relative w-full h-full">
          <ResponsiveContainer width="100%" height="100%">
            <RePieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius="75%"
                innerRadius={chartType === "donut" ? "55%" : "0%"}
                stroke="none"
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
                {...({
                  activeIndex: pieActiveIndex ?? undefined,
                  activeShape: renderActiveShape,
                } as any)}
              >
                {pieData.map((entry: any, i: number) => {
                  const isHovered = pieActiveIndex === i;
                  const isAnyHovered = pieActiveIndex !== null;
                  return (
                    <Cell
                      key={i}
                      fill={entry.fill}
                      style={{
                        cursor: "pointer",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        opacity: isAnyHovered && !isHovered ? 0.35 : 1,
                        filter: isHovered ? `drop-shadow(0px 0px 8px ${entry.fill})` : "none",
                      }}
                    />
                  );
                })}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </RePieChart>
          </ResponsiveContainer>
          {chartType === "donut" && (
            <div className="absolute inset-0 flex flex-col justify-center items-center pointer-events-none select-none">
              <span className="text-xl font-bold text-white font-mono tracking-tight">{fmtNum(total)}</span>
              <span className="text-[9px] text-gray-400 font-semibold uppercase tracking-wider mt-0.5">total</span>
            </div>
          )}
        </div>
      );
    }

    // 6. Bar / Horizontal Bar Charts
    if (chartType === "bar") {
      const chartWidth = rechartsData.length > 5 ? `${rechartsData.length * 75}px` : "100%";
      return (
        <div className="w-full h-full overflow-x-auto overflow-y-hidden custom-scroll-visible">
          <div style={{ width: chartWidth, minWidth: "100%", height: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rechartsData} margin={{ top: 12, right: 12, left: yAxisLabel ? 12 : -22, bottom: xAxisLabel ? 24 : 12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                <XAxis
                  dataKey="name"
                  stroke="#9ca3af"
                  fontSize={9}
                  tickLine={false}
                  interval={0}
                  tick={{ fontSize: 9, fill: "#9ca3af" }}
                  label={
                    xAxisLabel
                      ? { value: xAxisLabel, position: "insideBottom", offset: -2, fill: "#9ca3af", fontSize: 10, fontWeight: 600 }
                      : undefined
                  }
                />
                <YAxis
                  stroke="#9ca3af"
                  fontSize={9}
                  tickLine={false}
                  label={
                    yAxisLabel
                      ? { value: yAxisLabel, angle: -90, position: "insideLeft", offset: 10, fill: "#9ca3af", fontSize: 10, fontWeight: 600, style: { textAnchor: "middle" } }
                      : undefined
                  }
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255, 255, 255, 0.05)" }} />
                {valueKeys.map((col: string, i: number) => {
                  const isDimmedSeries = hoveredSeries !== null && cleanLbl(hoveredSeries) !== cleanLbl(col) && valueKeys.length > 1;
                  return (
                    <Bar key={col} dataKey={col} fill={SERIES_VARS[i % SERIES_VARS.length]} radius={[3, 3, 0, 0]} barSize={28}>
                      {rechartsData.map((rowItem: any, index: number) => {
                        const isCategoryMatch = hoveredSeries !== null && valueKeys.length === 1 && cleanLbl(rowItem.name) === cleanLbl(hoveredSeries);
                        const isHovered = hoveredIndex === index || isCategoryMatch || (hoveredSeries !== null && cleanLbl(hoveredSeries) === cleanLbl(col));
                        const isAnyHovered = hoveredIndex !== null || hoveredSeries !== null;
                        return (
                          <Cell
                            key={`cell-${index}`}
                            cursor="pointer"
                            fill={SERIES_VARS[i % SERIES_VARS.length]}
                            style={{
                              transition: "all 0.25s ease-in-out",
                              filter: isHovered
                                ? `brightness(1.3) drop-shadow(0px 0px 8px ${SERIES_VARS[i % SERIES_VARS.length]})`
                                : "none",
                              opacity: isDimmedSeries ? 0.2 : (isAnyHovered && !isHovered) ? 0.35 : 1,
                              transform: isHovered ? "translateY(-3px)" : "none",
                              transformOrigin: "bottom",
                            }}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                          />
                        );
                      })}
                    </Bar>
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    }

    if (chartType === "horizontal_bar") {
      const chartHeight = rechartsData.length > 5 ? `${rechartsData.length * 36}px` : "100%";
      return (
        <div className="w-full h-full overflow-y-auto overflow-x-hidden custom-scroll-visible">
          <div style={{ height: chartHeight, minHeight: "100%", width: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rechartsData} layout="vertical" margin={{ top: 8, right: 16, left: yAxisLabel ? 14 : -10, bottom: xAxisLabel ? 18 : 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" horizontal={false} />
                <XAxis
                  type="number"
                  stroke="#9ca3af"
                  fontSize={9}
                  tickLine={false}
                  label={
                    xAxisLabel
                      ? { value: xAxisLabel, position: "insideBottom", offset: -2, fill: "#9ca3af", fontSize: 10, fontWeight: 600 }
                      : undefined
                  }
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  stroke="#9ca3af"
                  fontSize={9}
                  tickLine={false}
                  width={85}
                  interval={0}
                  tick={{ fontSize: 9, fill: "#9ca3af" }}
                  label={
                    yAxisLabel
                      ? { value: yAxisLabel, angle: -90, position: "insideLeft", offset: 10, fill: "#9ca3af", fontSize: 10, fontWeight: 600, style: { textAnchor: "middle" } }
                      : undefined
                  }
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255, 255, 255, 0.05)" }} />
                {valueKeys.map((col: string, i: number) => {
                  const isDimmedSeries = hoveredSeries !== null && cleanLbl(hoveredSeries) !== cleanLbl(col) && valueKeys.length > 1;
                  return (
                    <Bar key={col} dataKey={col} fill={SERIES_VARS[i % SERIES_VARS.length]} radius={[0, 3, 3, 0]} barSize={14}>
                      {rechartsData.map((rowItem: any, index: number) => {
                        const isCategoryMatch = hoveredSeries !== null && valueKeys.length === 1 && cleanLbl(rowItem.name) === cleanLbl(hoveredSeries);
                        const isHovered = hoveredIndex === index || isCategoryMatch || (hoveredSeries !== null && cleanLbl(hoveredSeries) === cleanLbl(col));
                        const isAnyHovered = hoveredIndex !== null || hoveredSeries !== null;
                        return (
                          <Cell
                            key={`cell-${index}`}
                            cursor="pointer"
                            fill={SERIES_VARS[i % SERIES_VARS.length]}
                            style={{
                              transition: "all 0.25s ease-in-out",
                              filter: isHovered
                                ? `brightness(1.3) drop-shadow(0px 0px 8px ${SERIES_VARS[i % SERIES_VARS.length]})`
                                : "none",
                              opacity: isDimmedSeries ? 0.2 : (isAnyHovered && !isHovered) ? 0.35 : 1,
                              transform: isHovered ? "translateX(3px)" : "none",
                              transformOrigin: "left",
                            }}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                          />
                        );
                      })}
                    </Bar>
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    }

    // 7. Line & Area Charts
    if (chartType === "line") {
      const chartWidth = rechartsData.length > 5 ? `${rechartsData.length * 75}px` : "100%";
      return (
        <div className="w-full h-full overflow-x-auto overflow-y-hidden custom-scroll-visible">
          <div style={{ width: chartWidth, minWidth: "100%", height: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <ReLineChart data={rechartsData} margin={{ top: 14, right: 16, left: yAxisLabel ? 12 : -16, bottom: xAxisLabel ? 24 : 12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                <XAxis
                  dataKey="name"
                  stroke="#9ca3af"
                  fontSize={9}
                  tickLine={false}
                  interval={0}
                  tick={{ fontSize: 9, fill: "#9ca3af" }}
                  label={
                    xAxisLabel
                      ? { value: xAxisLabel, position: "insideBottom", offset: -2, fill: "#9ca3af", fontSize: 10, fontWeight: 600 }
                      : undefined
                  }
                />
                <YAxis
                  stroke="#9ca3af"
                  fontSize={9}
                  tickLine={false}
                  label={
                    yAxisLabel
                      ? { value: yAxisLabel, angle: -90, position: "insideLeft", offset: 10, fill: "#9ca3af", fontSize: 10, fontWeight: 600, style: { textAnchor: "middle" } }
                      : undefined
                  }
                />
                <Tooltip content={<CustomTooltip />} />
                {valueKeys.map((col: string, i: number) => {
                  const isDimmed = hoveredSeries !== null && cleanLbl(hoveredSeries) !== cleanLbl(col);
                  const isHighlighted = hoveredSeries !== null && cleanLbl(hoveredSeries) === cleanLbl(col);
                  const seriesColor = SERIES_VARS[i % SERIES_VARS.length];
                  return (
                    <Line
                      key={col}
                      type="monotone"
                      dataKey={col}
                      stroke={seriesColor}
                      strokeWidth={isHighlighted ? 4.5 : 2}
                      opacity={isDimmed ? 0.2 : 1}
                      dot={{
                        r: isHighlighted ? 5 : 3.5,
                        fill: seriesColor,
                        stroke: "#ffffff",
                        strokeWidth: isHighlighted ? 2 : 1.2,
                      }}
                      activeDot={{
                        r: 6.5,
                        fill: seriesColor,
                        stroke: "#ffffff",
                        strokeWidth: 2.5,
                        style: { filter: "drop-shadow(0px 0px 6px rgba(255, 255, 255, 0.9))" }
                      }}
                      style={{
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        filter: isHighlighted ? `drop-shadow(0px 0px 8px ${seriesColor})` : "none",
                      }}
                    />
                  );
                })}
              </ReLineChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    }

    if (chartType === "area") {
      const chartWidth = rechartsData.length > 5 ? `${rechartsData.length * 75}px` : "100%";
      return (
        <div className="w-full h-full overflow-x-auto overflow-y-hidden custom-scroll-visible">
          <div style={{ width: chartWidth, minWidth: "100%", height: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <ReAreaChart data={rechartsData} margin={{ top: 14, right: 16, left: yAxisLabel ? 12 : -16, bottom: xAxisLabel ? 24 : 12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                <XAxis
                  dataKey="name"
                  stroke="#9ca3af"
                  fontSize={9}
                  tickLine={false}
                  interval={0}
                  tick={{ fontSize: 9, fill: "#9ca3af" }}
                  label={
                    xAxisLabel
                      ? { value: xAxisLabel, position: "insideBottom", offset: -2, fill: "#9ca3af", fontSize: 10, fontWeight: 600 }
                      : undefined
                  }
                />
                <YAxis
                  stroke="#9ca3af"
                  fontSize={9}
                  tickLine={false}
                  label={
                    yAxisLabel
                      ? { value: yAxisLabel, angle: -90, position: "insideLeft", offset: 10, fill: "#9ca3af", fontSize: 10, fontWeight: 600, style: { textAnchor: "middle" } }
                      : undefined
                  }
                />
                <Tooltip content={<CustomTooltip />} />
                {valueKeys.map((col: string, i: number) => {
                  const isDimmed = hoveredSeries !== null && cleanLbl(hoveredSeries) !== cleanLbl(col);
                  const isHighlighted = hoveredSeries !== null && cleanLbl(hoveredSeries) === cleanLbl(col);
                  const seriesColor = SERIES_VARS[i % SERIES_VARS.length];
                  return (
                    <Area
                      key={col}
                      type="monotone"
                      dataKey={col}
                      stroke={seriesColor}
                      fill={seriesColor}
                      fillOpacity={isHighlighted ? 0.55 : isDimmed ? 0.05 : 0.18}
                      strokeWidth={isHighlighted ? 4.5 : 2}
                      opacity={isDimmed ? 0.2 : 1}
                      dot={{
                        r: isHighlighted ? 5 : 3.5,
                        fill: seriesColor,
                        stroke: "#ffffff",
                        strokeWidth: isHighlighted ? 2 : 1.2,
                      }}
                      activeDot={{
                        r: 6.5,
                        fill: seriesColor,
                        stroke: "#ffffff",
                        strokeWidth: 2.5,
                        style: { filter: "drop-shadow(0px 0px 6px rgba(255, 255, 255, 0.9))" }
                      }}
                      style={{
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        filter: isHighlighted ? `drop-shadow(0px 0px 8px ${seriesColor})` : "none",
                      }}
                    />
                  );
                })}
              </ReAreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div ref={containerRef} className="w-full h-full flex flex-col justify-between">
      <div className="flex-1 min-h-0 relative">
        {renderChartContent()}
        {legendList.length > 0 && renderLegend(legendList)}
      </div>
      {renderTooltip()}
    </div>
  );
}
